import type { ComposerPanel, ComposerPanelKind } from "@bash/shared";

/**
 * The kinds a host DRAWS as a card (composer panels, spec 2026-09-19 §3.4).
 * The sheet, the receipt, and the connect card that wears the paused agent's
 * own face (ConnectPanel). history and tray are store actions (the pill's
 * history, the Island) or a words line; close never stays; confirm is still
 * Train 2, waiting on its renderer. A leaf: the crown's model reads it without
 * a component behind it.
 */
export const DRAWN_KINDS: ReadonlySet<ComposerPanelKind> =
  new Set<ComposerPanelKind>(["sheet", "receipt", "connect"]);

/**
 * A panel's identity for React. A deck holds at most one panel of a kind, so
 * the kind is the key for every card that carries no state of its own. The
 * receipt carries some — the Undo chip's gone / busy / spoken words — and a
 * LATER receipt replacing an earlier one in a mounted deck must arrive as a
 * NEW card, or the fresh run inherits the old one's chip: its words, or no
 * Undo at all once the first receipt's deadline has passed. The run is the
 * identity; a receipt with no run id falls back to its deadline, then its
 * title, so two receipts in a row still differ.
 *
 * The connect card carries state the same way (the probe's baseline, clicked,
 * connected), and its identity is the TOOLKIT: a connect for another app must
 * mount fresh, or the second card inherits the first's Connected.
 */
export function panelKey(panel: ComposerPanel): string {
  if (panel.kind === "connect") return `connect:${panel.toolkit}`;
  if (panel.kind !== "receipt") return panel.kind;
  return `receipt:${panel.runId ?? panel.undoUntil ?? panel.title}`;
}

/** The kinds a host with no history and no tray prints as a words line. */
const WORDS_KINDS: ReadonlySet<ComposerPanelKind> = new Set<ComposerPanelKind>([
  "history",
  "tray",
]);

/**
 * Does this deck put anything on the screen? A host wraps its deck in padding,
 * so the wrapper rides this rather than the list being set: a kind nobody
 * draws (Train 2's confirm, kept by the reducer with no producer today) would
 * otherwise leave a persistent empty box above the thread.
 * `words` is the host that prints history and tray instead of opening them.
 */
export function deckDraws(
  panels: ComposerPanel[] | undefined,
  words = false
): boolean {
  return (panels ?? []).some(
    (p) => DRAWN_KINDS.has(p.kind) || (words && WORDS_KINDS.has(p.kind))
  );
}
